import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppState, Thunk } from "../App";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { RequestStatus } from "./requestStatus";
import { getThreediModel, getTwoDExtendModel } from "./simulation";

interface RasterDataUrls {
  layer: string;
  tms: {
    url: string;
  };
  minmax: {
    url: string;
  };
  profile: {
    url: string;
  };
  valid_from: number;
}

interface MinMax {
  defaultMin: number;
  defaultMax: number;
  min: number;
  max: number;
  layer: string;
}

interface ReceiveRasterUrlsMessage {
  type: string;
  data: RasterDataUrls;
}

// for now only contains source rasters
interface LayerRasterSourcesMenu {
  status: RequestStatus;
  failedMessage: string;
  list: api.Raster[];
  rasterDataUrls: RasterDataUrls[];
  minMaxForLayers: MinMax[];
}

const INITIAL_STATE: LayerRasterSourcesMenu = {
  status: "NOT_SENT",
  failedMessage: "",
  list: [],
  rasterDataUrls: [],
  minMaxForLayers: [],
};

export const getStatus = (state: AppState) => state.layerRasterSourcesMenu.status;
export const getFailedMessage = (state: AppState) => state.layerRasterSourcesMenu.failedMessage;
export const getList = (state: AppState) => state.layerRasterSourcesMenu.list;
export const getMinMaxForLayers = (state: AppState) => state.layerRasterSourcesMenu.minMaxForLayers;
export const getMinMaxUrls = (state: AppState) => state.layerRasterSourcesMenu.rasterDataUrls;

const SET_STATUS = "LayerRasterSourcesMenu/SET_STATUS";
const SET_LIST = "LayerRasterSourcesMenu/SET_LIST";
const RECEIVED_SOURCE_RASTER_URLS = "LayerRasterSourcesMenu/RECEIVED_SOURCE_RASTER_URLS";
const SET_MIN_MAX = "LayerRasterSourcesMenu/SET_MIN_MAX";

interface SetStatus {
  type: typeof SET_STATUS;
  failedMessage: string;
  status: RequestStatus;
}

interface SetList {
  type: typeof SET_LIST;
  list: api.Raster[];
  failedMessage: string;
  status: RequestStatus;
}

interface ReceiveSourceRasterUrls {
  type: typeof RECEIVED_SOURCE_RASTER_URLS;
  payload: ReceiveRasterUrlsMessage;
}

interface SetMinMax {
  type: typeof SET_MIN_MAX;
  payload: MinMax;
}

export const setStatus = (message: string, status: RequestStatus) => {
  return {
    type: SET_STATUS,
    failedMessage: message,
    status: status,
  };
};

export const setList = (list: api.Raster[], message: string, status: RequestStatus) => {
  return {
    type: SET_LIST,
    status: status,
    failedMessage: message,
    list: list,
  };
};

type LayerRasterSourcesMenuAction =
  | SetStatus
  | SetList
  | ReceiveSourceRasterUrls
  | BackToStartScreen
  | SetMinMax;

export const receiveSourceRasterUrls =
  (parsed: ReceiveRasterUrlsMessage): Thunk =>
  (dispatch, getState) => {
    dispatch({
      type: RECEIVED_SOURCE_RASTER_URLS,
      payload: parsed,
    });
    if (parsed.data.minmax) {
      const minMaxUrl = parsed.data.minmax.url;
      const simulationBbox = getTwoDExtendModel(getState());
      if (simulationBbox) {
        const coordinatesString =
          "" +
          simulationBbox.coordinates[0][0] +
          "," +
          simulationBbox.coordinates[0][1] +
          "," +
          simulationBbox.coordinates[1][0] +
          "," +
          simulationBbox.coordinates[1][1];
        const bboxUrl = minMaxUrl.replace("{bbox}", coordinatesString);
        const epsgUrl = bboxUrl.replace("{epsg}", "4326"); //4326
        fetch(epsgUrl)
          .then((response) => {
            return response.json();
          })
          .then((parsedResponse) => {
            dispatch(
              setMinMax({
                defaultMin: parsedResponse.minimum,
                defaultMax: parsedResponse.maximum,
                min: parsedResponse.minimum,
                max: parsedResponse.maximum,
                layer: parsed.data.layer,
              })
            );
          });
      }
    }
  };

export const setMinMax = (minMax: MinMax) => {
  return {
    type: SET_MIN_MAX,
    payload: minMax,
  };
};

export const retrieveConfiguredRasters = (): Thunk => (dispatch, getState) => {
  const state = getState();
  const simulationModel = getThreediModel(state);
  const simulationId = (simulationModel && simulationModel.id) || null;
  if (simulationId === null) {
    console.error("retrieving layers while a model is not yet chosen shouldnot happen");
    return;
  }

  dispatch(setStatus("", "SENT"));
  const v3Api = getV3Api();
  v3Api
    .threedimodelsRastersList({
      threedimodelPk: simulationId,
      limit: 100,
    })
    .then((response) => {
      dispatch(setList(response.results, "", "SUCCESS"));
      console.log("rain retrieved from api. response", response);
    })
    .catch((error) => {
      dispatch(
        setStatus(
          "retrieving existing rain events failed with following error. Check console and network tab" +
            error,
          "FAILED"
        )
      );
      console.log("retrieving existing rain events failed with following error  ____", error);
    });
};

export default function reducer(state = INITIAL_STATE, action: LayerRasterSourcesMenuAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_STATUS: {
      // use acoulades for block scoping the consts status message
      const { status, failedMessage } = action;
      return {
        ...state,
        status,
        failedMessage,
      };
    }
    case SET_LIST: {
      const { status, failedMessage, list } = action;
      return {
        ...state,
        status,
        failedMessage,
        list,
      };
    }
    case RECEIVED_SOURCE_RASTER_URLS: {
      const data = action.payload.data;
      const typeToUpdate = data.layer;
      const originalList = [...state.rasterDataUrls];
      const finalList = originalList.filter((item) => item.layer !== typeToUpdate);
      finalList.push(data);
      return {
        ...state,
        rasterDataUrls: finalList,
      };
    }
    case SET_MIN_MAX: {
      const data = action.payload;
      const typeToUpdate = data.layer;
      const originalList = [...state.minMaxForLayers];
      const finalList = originalList.filter((item) => item.layer !== typeToUpdate);
      finalList.push(data);
      return {
        ...state,
        minMaxForLayers: finalList,
      };
    }
    default:
      return state;
  }
}
